import React, { useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { optionsStatusProps, useOptions } from '../hooks/useOptions';
import { usePolicyList, useSubmit } from '../hooks/usePolicyList';
import { ResultViewer } from '../components/ResultViewer';
import { PolicyModal } from '../components/PolicyModal';
import { UserFields } from '../components/UserFields';
import { deleteColumn } from '../components/deleteColumn';
import {
  USER_ROLE_RESOURCE_COLUMNS,
  UserRoleResourceRow,
  resourceOptions,
  toNamedOptions,
  toResourceChoices,
  toUserRoleResourceRows,
} from './policyRows';

export const UserRoleResourcePage: React.FC<{ app: string }> = ({ app }) => {
  const { base, loading, result, reload } = usePolicyList(app, 'user_role_resource');
  const { addNotification } = useNotifications();
  const rows = useMemo(() => toUserRoleResourceRows(result?.data), [result]);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [userType, setUserType] = useState('PERSON');
  const [user, setUser] = useState('');
  const [roleName, setRoleName] = useState('');
  const [resourcePaths, setResourcePaths] = useState<readonly MultiselectProps.Option[]>([]);
  const roles = useOptions(`/${encodeURIComponent(app)}/roles`, toNamedOptions);
  const resources = useOptions(`/${encodeURIComponent(app)}/resources`, toResourceChoices);
  const create = useSubmit();

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<UserRoleResourceRow | null>(null);
  const del = useSubmit();

  const columns = useMemo(
    () => [
      ...USER_ROLE_RESOURCE_COLUMNS,
      deleteColumn<UserRoleResourceRow>(
        r => (r.userType && r.user && r.role && r.resource ? `${r.userType} ${r.user} ${r.role} on ${r.resourcePath}` : null),
        r => {
          del.setError(null);
          setPendingDelete(r);
        }
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setError and setPendingDelete are stable
    []
  );

  if (!app) return <Alert type="info">Select an application from the top bar.</Alert>;

  const openCreate = () => {
    setUserType('PERSON');
    setUser('');
    setRoleName('');
    setResourcePaths([]);
    create.setError(null);
    setCreateOpen(true);
    roles.load();
    resources.load();
  };

  const confirmCreate = async () => {
    const value = user.trim();
    const paths = resourcePaths.map(o => o.value!);
    const chosen = resources.state.items.filter(c => paths.includes(c.path)).map(c => c.resource);
    const ok = await create.submit(() =>
      api('PUT', base, {
        users: [{ type: userType, value }],
        roles: [{ name: roleName }],
        resources: chosen,
      })
    );
    if (ok) {
      setCreateOpen(false);
      addNotification('success', `Granted ${roleName} on ${paths.join(', ')} to ${userType} ${value}`);
      await reload();
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { userType: type, user: value, role, resource, resourcePath: path } = pendingDelete;
    const ok = await del.submit(() =>
      api('DELETE', base, { user: { type, value }, role: { name: role }, resource })
    );
    if (ok) {
      setPendingDelete(null);
      addNotification('success', `Removed ${role} on ${path} from ${type} ${value}`);
      await reload();
    }
  };

  const selectedRole = roleName
    ? roles.state.items.find(o => o.value === roleName) ?? { value: roleName, label: roleName }
    : null;
  const canCreate = user.trim() !== '' && roleName !== '' && resourcePaths.length > 0;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <Button variant="primary" onClick={openCreate}>
            Create Policy
          </Button>
        }
      >
        User - Role - Resource Policy
      </Header>

      {loading && !result ? (
        <StatusIndicator type="loading">Loading policies</StatusIndicator>
      ) : (
        <ResultViewer data={result?.data} error={result?.error} rows={rows} columns={columns} title="Policy" />
      )}

      <PolicyModal
        visible={createOpen}
        header="Create policy"
        confirmText="Create"
        busy={create.busy}
        confirmDisabled={!canCreate}
        error={create.error}
        errorHeader="Create failed"
        onCancel={() => setCreateOpen(false)}
        onConfirm={confirmCreate}
      >
        <UserFields userType={userType} user={user} onUserTypeChange={setUserType} onUserChange={setUser} />
        <FormField label="Role">
          <Select
            selectedOption={selectedRole}
            onChange={({ detail }) => setRoleName(detail.selectedOption.value ?? '')}
            options={roles.state.items}
            {...optionsStatusProps(roles.state, 'roles')}
            placeholder="Choose a role"
            ariaLabel="Role"
          />
        </FormField>
        <FormField label="Resources">
          <Multiselect
            selectedOptions={resourcePaths}
            onChange={({ detail }) => setResourcePaths(detail.selectedOptions)}
            options={resourceOptions(resources.state.items)}
            {...optionsStatusProps(resources.state, 'resources')}
            placeholder="Choose resources"
            ariaLabel="Resources"
          />
        </FormField>
      </PolicyModal>

      <PolicyModal
        visible={pendingDelete !== null}
        header="Delete policy"
        confirmText="Delete"
        busy={del.busy}
        error={del.error}
        errorHeader="Delete failed"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      >
        {pendingDelete && (
          <Box>
            Remove role <b>{pendingDelete.role}</b> on <b>{pendingDelete.resourcePath}</b> from{' '}
            <b>{pendingDelete.userType} {pendingDelete.user}</b> in <b>{app}</b>?
          </Box>
        )}
      </PolicyModal>
    </SpaceBetween>
  );
};
